/**
 * cart-purchase-bonus.js
 * ================================================================
 * ご購入特典機能 v7（シンプル版）
 * ================================================================
 *
 * 設計方針：
 *   - 判定ロジックは Liquid 側が担う（cart-campaign-items.liquid）
 *   - JS は Liquid 出力の data-action="add"/"remove" を見て実行するだけ
 *   - 数量変更時の整合性はユーザーが「カートを更新」ボタンを押して
 *     リロードする運用（汎用ボタンとして main-cart.liquid に inline 配置）
 *
 * 動作フロー：
 *   [ページロード時]
 *     - DOMContentLoaded で [data-purchase-bonus] 要素を走査
 *     - data-action="add" の要素は /cart/add.js を実行
 *     - data-action="remove" の要素は /cart/change.js を実行
 *     - 変更があった場合はガード付きリロード
 *
 * data 属性：
 *   data-block-id        : block.id
 *   data-action          : 'add' | 'remove' | 'none'
 *   data-add-variant-id  : 追加すべき variant id（add時）
 *   data-remove-key      : カート内 line item の key（remove時）
 *   data-sold-out        : "1" の場合、売り切れ（add しない保険）
 *
 * ガード機構：
 *   - URL ?bonus_synced=1 + sessionStorage TTL の二重ガード
 *   - 自分が発行した fetch は X-Purchase-Bonus-Internal: 1 で識別
 * ================================================================
 */
(function () {
  'use strict';

  var LOG_PREFIX = '[purchase-bonus]';
  var GUARD_KEY = '__purchaseBonusInFlight__';
  var GUARD_TTL_MS = 8000;
  var URL_GUARD_PARAM = 'bonus_synced';
  var RETRY_COUNT = 1;
  var RETRY_DELAY_MS = 400;
  var DEBUG = false; // 本番リリース時は false

  function log() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift(LOG_PREFIX);
    console.log.apply(console, args);
  }

  function warn() {
    var args = Array.prototype.slice.call(arguments);
    args.unshift(LOG_PREFIX);
    console.warn.apply(console, args);
  }

  function sleep(ms) {
    return new Promise(function (r) { setTimeout(r, ms); });
  }

  /* ============================================================
     ガード管理
  ============================================================ */
  function hasUrlGuard() {
    try {
      var params = new URLSearchParams(window.location.search);
      return params.get(URL_GUARD_PARAM) === '1';
    } catch (e) {
      return false;
    }
  }

  function consumeUrlGuard() {
    try {
      var url = new URL(window.location.href);
      url.searchParams.delete(URL_GUARD_PARAM);
      window.history.replaceState({}, '', url.toString());
    } catch (e) {}
  }

  function isSessionGuardActive() {
    try {
      var raw = sessionStorage.getItem(GUARD_KEY);
      if (!raw) return false;
      var ts = parseInt(raw, 10);
      if (isNaN(ts)) return false;
      return (Date.now() - ts) < GUARD_TTL_MS;
    } catch (e) {
      return false;
    }
  }

  function setSessionGuard() {
    try { sessionStorage.setItem(GUARD_KEY, String(Date.now())); } catch (e) {}
  }

  function clearSessionGuard() {
    try { sessionStorage.removeItem(GUARD_KEY); } catch (e) {}
  }

  function reloadWithGuard() {
    setSessionGuard();
    try {
      var url = new URL(window.location.href);
      url.searchParams.set(URL_GUARD_PARAM, '1');
      window.location.replace(url.toString());
    } catch (e) {
      window.location.reload();
    }
  }

  /* ============================================================
     Ajax wrapper（add/remove）
  ============================================================ */
  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Purchase-Bonus-Internal': '1'
      },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    });
  }

  async function tryAdd(variantId, blockId) {
    var res = await postJSON('/cart/add.js', {
      items: [{
        id: parseInt(variantId, 10),
        quantity: 1,
        properties: { _bonus_block_id: blockId }
      }]
    });
    if (!res.ok) {
      var errBody = await res.text();
      throw new Error('add failed: ' + res.status + ' ' + errBody);
    }
    return true;
  }

  async function tryRemove(key) {
    var res = await postJSON('/cart/change.js', {
      id: key,
      quantity: 0
    });
    if (!res.ok) {
      var errBody = await res.text();
      throw new Error('remove failed: ' + res.status + ' ' + errBody);
    }
    return true;
  }

  async function addBonus(variantId, blockId) {
    log('adding bonus', { variantId: variantId, blockId: blockId });
    for (var i = 0; i <= RETRY_COUNT; i++) {
      try {
        await tryAdd(variantId, blockId);
        log('add ok', { blockId: blockId, attempt: i + 1 });
        return true;
      } catch (e) {
        warn('add attempt failed', { blockId: blockId, attempt: i + 1, error: e.message });
        if (i < RETRY_COUNT) await sleep(RETRY_DELAY_MS);
      }
    }
    return false;
  }

  async function removeBonus(key) {
    log('removing bonus', { key: key });
    for (var i = 0; i <= RETRY_COUNT; i++) {
      try {
        await tryRemove(key);
        log('remove ok', { key: key, attempt: i + 1 });
        return true;
      } catch (e) {
        warn('remove attempt failed', { key: key, attempt: i + 1, error: e.message });
        if (i < RETRY_COUNT) await sleep(RETRY_DELAY_MS);
      }
    }
    return false;
  }

  /* ============================================================
     bonus 要素の処理（add / remove の振り分け）
  ============================================================ */
  function processSingle(el) {
    var action = el.getAttribute('data-action');
    var blockId = el.getAttribute('data-block-id');
    var soldOut = el.getAttribute('data-sold-out') === '1';

    log('processing', { blockId: blockId, action: action, soldOut: soldOut });

    if (action === 'add' && soldOut) {
      warn('skipping add: sold out', { blockId: blockId });
      return Promise.resolve(false);
    }
    if (action === 'add') {
      var variantId = el.getAttribute('data-add-variant-id');
      if (!variantId) {
        warn('add but no variant id', { blockId: blockId });
        return Promise.resolve(false);
      }
      return addBonus(variantId, blockId);
    }
    if (action === 'remove') {
      var key = el.getAttribute('data-remove-key');
      if (!key) {
        warn('remove but no remove key', { blockId: blockId });
        return Promise.resolve(false);
      }
      return removeBonus(key);
    }
    return Promise.resolve(false);
  }

  /* ============================================================
     カート整合性チェック：通常商品 0 で特典のみ残っている状態を
     検出した場合、特典を強制削除してリロード
     （Liquid 側のロジックで data-action="remove" が出ない状況や、
      Ajax 同期失敗時の保険）
  ============================================================ */
  async function enforceCartConsistency() {
    try {
      var res = await fetch('/cart.js', {
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin'
      });
      if (!res.ok) return false;
      var cart = await res.json();

      var normalCount = 0;
      var bonusKeys = [];
      cart.items.forEach(function (item) {
        if (item.properties && item.properties._bonus_block_id) {
          bonusKeys.push(item.key);
        } else {
          normalCount += item.quantity;
        }
      });

      if (normalCount === 0 && bonusKeys.length > 0) {
        log('normal count is 0, removing all bonuses', bonusKeys);
        var promises = bonusKeys.map(function (key) {
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-Purchase-Bonus-Internal': '1'
            },
            credentials: 'same-origin',
            body: JSON.stringify({ id: key, quantity: 0 })
          });
        });
        await Promise.allSettled(promises);
        return true; // mutated
      }
      return false;
    } catch (e) {
      warn('enforceCartConsistency error', e);
      return false;
    }
  }

  /* ============================================================
     ページロード時の処理
  ============================================================ */
  async function processBonusesOnLoad() {
    // URL ガード判定（リロード直後の二重処理防止）
    if (hasUrlGuard()) {
      log('url guard found, consuming');
      clearSessionGuard();
      consumeUrlGuard();
      return;
    }
    if (isSessionGuardActive()) {
      log('session guard active, skipping');
      clearSessionGuard();
      return;
    }

    // ★ 整合性チェック：通常商品 0 で特典のみ状態を強制解消
    var consistencyMutated = await enforceCartConsistency();
    if (consistencyMutated) {
      log('cart consistency enforced, reloading');
      reloadWithGuard();
      return;
    }

    var nodes = document.querySelectorAll('[data-purchase-bonus]');
    if (!nodes.length) {
      log('no bonus nodes');
      return;
    }

    var targets = [];
    for (var i = 0; i < nodes.length; i++) {
      var action = nodes[i].getAttribute('data-action');
      if (action === 'add' || action === 'remove') {
        targets.push(nodes[i]);
      }
    }
    if (!targets.length) {
      log('no actionable bonus nodes');
      return;
    }

    log('processing', targets.length, 'bonus(es)');

    var results = await Promise.allSettled(targets.map(processSingle));
    var mutated = results.some(function (r) {
      return r.status === 'fulfilled' && r.value === true;
    });

    if (mutated) {
      log('mutated, reloading with guard');
      reloadWithGuard();
    } else {
      log('no mutation, done');
      clearSessionGuard();
    }
  }

  /* ============================================================
     初期化
  ============================================================ */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', processBonusesOnLoad);
  } else {
    processBonusesOnLoad();
  }
})();
